import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Breadcrumb, BreadcrumbItem, Spinner } from '../../../shared/components';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionCodes } from '../../auth/permissions';
import { useToast } from '../../notifications';
import { getAiProviderSettings } from '../../admin/services/aiPromptConfigService';
import type { AiProviderSettings } from '../../admin/types/AiPromptConfig';
import {
  createAiToolOrchestrator,
  deleteAiToolOrchestrator,
  getAiToolOrchestratorCatalog,
  listAiToolOrchestrators,
  previewAiToolOrchestrator,
  updateAiToolOrchestrator,
} from '../services/orchestratorService';
import type {
  AiToolOrchestrator,
  AiToolOrchestratorArgumentMode,
  AiToolOrchestratorCatalogItem,
  AiToolOrchestratorParameter,
} from '../types/Orchestrator';
import styles from './OrchestratorsPage.module.css';
import {
  buildSubagentModelOptions,
  isSubagentModelSelectable,
  resolveSubagentModelIdForProvider,
} from '../utils/subagentModelOptions';
import { ToolUseCaseAssignment } from '../../tools/components/ToolUseCaseAssignment';
import { DEFAULT_ASSIGNED_USE_CASE_IDS } from '../../tools/constants/assignableUseCases';
import { getToolUseCaseAssignments } from '../../tools/services/toolUseCaseAssignmentService';

const emptyParameter = (): AiToolOrchestratorParameter => ({
  name: '',
  description: '',
  isRequired: false,
  defaultValue: '',
  example: '',
});

const emptyForm = {
  orchestratorId: '',
  kernelFunctionName: '',
  displayName: '',
  description: '',
  isPublished: false,
  childFunctionIds: [] as string[],
  systemPrompt: '',
  argumentMode: 'forward' as AiToolOrchestratorArgumentMode,
  parameters: [emptyParameter()],
  subagentChatProvider: 'inherit',
  subagentModelId: '',
  maxChildToolCalls: 5,
  maxOutputTokens: '',
  timeoutSeconds: '',
  forwardedParameterName: 'query',
  assignedUseCaseIds: [...DEFAULT_ASSIGNED_USE_CASE_IDS],
};

export const OrchestratorsPage: React.FC = () => {
  const { t } = useTranslation(['orchestrators', 'common']);
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const canManage = hasPermission(PermissionCodes.aiOrchestratorsManage);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [orchestrators, setOrchestrators] = useState<AiToolOrchestrator[]>([]);
  const [catalog, setCatalog] = useState<AiToolOrchestratorCatalogItem[]>([]);
  const [providerSettings, setProviderSettings] = useState<AiProviderSettings | null>(null);
  const [selectedId, setSelectedId] = useState<string>('');
  const [form, setForm] = useState(emptyForm);
  const [previewArgs, setPreviewArgs] = useState<Record<string, string>>({});
  const [previewResult, setPreviewResult] = useState('');

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: t('ai', { ns: 'nav', defaultValue: 'AI' }), path: '/ai/knowledge' },
    { label: t('title'), path: '/ai/subagents' },
  ];

  const selectedOrchestrator = useMemo(
    () => orchestrators.find((item) => item.orchestratorId === selectedId) ?? null,
    [orchestrators, selectedId],
  );

  const assignableCatalog = useMemo(
    () => catalog.filter((item) => item.isAssignableAsChild || form.childFunctionIds.includes(item.functionId)),
    [catalog, form.childFunctionIds],
  );

  const subagentModelOptions = useMemo(
    () => buildSubagentModelOptions(providerSettings, form.subagentChatProvider),
    [providerSettings, form.subagentChatProvider],
  );

  const subagentModelSelectDisabled = !canManage
    || saving
    || !isSubagentModelSelectable(form.subagentChatProvider)
    || subagentModelOptions.length === 0;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [orchestratorData, catalogData, providerSettingsData] = await Promise.all([
        listAiToolOrchestrators(),
        getAiToolOrchestratorCatalog(),
        getAiProviderSettings().catch(() => null),
      ]);
      setOrchestrators(orchestratorData);
      setCatalog(catalogData);
      setProviderSettings(providerSettingsData);
    } catch {
      showToast(t('failedToLoad'), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, t]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (!selectedOrchestrator?.functionId) {
      return undefined;
    }

    let cancelled = false;
    void getToolUseCaseAssignments(selectedOrchestrator.functionId)
      .then((assignedUseCaseIds) => {
        if (!cancelled) {
          setForm((current) => (
            current.orchestratorId === selectedOrchestrator.orchestratorId
              ? { ...current, assignedUseCaseIds }
              : current
          ));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setForm((current) => (
            current.orchestratorId === selectedOrchestrator.orchestratorId
              ? { ...current, assignedUseCaseIds: [] }
              : current
          ));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedOrchestrator?.functionId, selectedOrchestrator?.orchestratorId]);

  const resetToCreate = () => {
    setSelectedId('');
    setForm(emptyForm);
    setPreviewArgs({});
    setPreviewResult('');
  };

  const loadIntoForm = (orchestrator: AiToolOrchestrator) => {
    setSelectedId(orchestrator.orchestratorId);
    setForm({
      orchestratorId: orchestrator.orchestratorId,
      kernelFunctionName: orchestrator.kernelFunctionName,
      displayName: orchestrator.displayName,
      description: orchestrator.description,
      isPublished: orchestrator.isPublished,
      childFunctionIds: [...orchestrator.childFunctionIds],
      systemPrompt: orchestrator.systemPrompt,
      argumentMode: (orchestrator.argumentMode as AiToolOrchestratorArgumentMode) || 'forward',
      parameters: orchestrator.parameters.length > 0 ? orchestrator.parameters : [emptyParameter()],
      subagentChatProvider: orchestrator.subagentChatProvider || 'inherit',
      subagentModelId: resolveSubagentModelIdForProvider(
        providerSettings,
        orchestrator.subagentChatProvider || 'inherit',
        orchestrator.subagentModelId ?? '',
      ),
      maxChildToolCalls: orchestrator.maxChildToolCalls,
      maxOutputTokens: orchestrator.maxOutputTokens?.toString() ?? '',
      timeoutSeconds: orchestrator.timeoutSeconds?.toString() ?? '',
      forwardedParameterName: orchestrator.forwardedParameterName || 'query',
      assignedUseCaseIds: [...DEFAULT_ASSIGNED_USE_CASE_IDS],
    });
    setPreviewArgs({});
    setPreviewResult('');
  };

  const toggleChildTool = (functionId: string) => {
    setForm((current) => {
      const exists = current.childFunctionIds.includes(functionId);
      return {
        ...current,
        childFunctionIds: exists
          ? current.childFunctionIds.filter((id) => id !== functionId)
          : [...current.childFunctionIds, functionId],
      };
    });
  };

  const buildPayload = () => ({
    orchestratorId: form.orchestratorId || undefined,
    kernelFunctionName: form.kernelFunctionName.trim(),
    displayName: form.displayName.trim(),
    description: form.description.trim(),
    isPublished: form.isPublished,
    childFunctionIds: form.childFunctionIds,
    systemPrompt: form.systemPrompt.trim(),
    argumentMode: form.argumentMode,
    parameters: form.argumentMode === 'forward'
      ? []
      : form.parameters.filter((parameter) => parameter.name.trim()),
    subagentChatProvider: form.subagentChatProvider,
    subagentModelId: form.subagentModelId.trim() || undefined,
    maxChildToolCalls: Number(form.maxChildToolCalls) || 5,
    maxOutputTokens: form.maxOutputTokens ? Number(form.maxOutputTokens) : undefined,
    timeoutSeconds: form.timeoutSeconds ? Number(form.timeoutSeconds) : undefined,
    forwardedParameterName: form.forwardedParameterName.trim() || 'query',
    assignedUseCaseIds: form.assignedUseCaseIds,
  });

  const handleSave = async () => {
    if (!canManage) return;
    setSaving(true);
    try {
      const payload = buildPayload();
      if (selectedId) {
        await updateAiToolOrchestrator(selectedId, payload);
        showToast(t('updated'), 'success');
      } else {
        const created = await createAiToolOrchestrator(payload);
        setSelectedId(created.orchestratorId);
        showToast(t('created'), 'success');
      }
      await loadData();
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToSave'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!canManage || !selectedId) return;
    if (!window.confirm(t('confirmDelete'))) return;
    setSaving(true);
    try {
      await deleteAiToolOrchestrator(selectedId);
      showToast(t('deleted'), 'success');
      resetToCreate();
      await loadData();
    } catch {
      showToast(t('failedToDelete'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!selectedId) return;
    setPreviewing(true);
    try {
      const result = await previewAiToolOrchestrator(selectedId, {
        arguments: previewArgs,
      });
      setPreviewResult(result);
    } catch {
      showToast(t('failedToPreview'), 'error');
    } finally {
      setPreviewing(false);
    }
  };

  const argumentFields = form.argumentMode === 'forward'
    ? [{ name: form.forwardedParameterName || 'query', required: true }]
    : form.parameters.filter((parameter) => parameter.name.trim());

  return (
    <div className="company-admin-page">
      <Breadcrumb items={breadcrumbItems} />

      <div className="company-page-head company-page-head--row">
        <div>
          <h2>{t('title')}</h2>
          <p className="company-page-head-description">{t('subtitle')}</p>
        </div>
        {canManage ? (
          <button type="button" className="btn btn-secondary" onClick={resetToCreate}>
            {t('createOrchestrator')}
          </button>
        ) : null}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
          <Spinner text={t('loading', { ns: 'common' })} />
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 320px) 1fr', gap: 16 }}>
          <section className="company-section">
            <h3>{t('orchestratorList')}</h3>
            <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
              {orchestrators.map((orchestrator) => (
                <button
                  key={orchestrator.orchestratorId}
                  type="button"
                  className={`btn btn-secondary ${selectedId === orchestrator.orchestratorId ? 'active' : ''}`}
                  style={{ textAlign: 'left' }}
                  onClick={() => loadIntoForm(orchestrator)}
                >
                  <strong>{orchestrator.displayName}</strong>
                  <div style={{ color: '#64748b', fontSize: 12 }}>
                    {orchestrator.childFunctionIds.length} {t('childTools')}
                    {orchestrator.isPublished ? ` · ${t('published')}` : ` · ${t('draft')}`}
                  </div>
                </button>
              ))}
              {orchestrators.length === 0 ? <div>{t('noOrchestrators')}</div> : null}
            </div>
          </section>

          <section className="company-section">
            <h3>{selectedOrchestrator ? t('editOrchestrator') : t('createOrchestrator')}</h3>

            <label
              className={[
                styles.publishedToggle,
                form.isPublished ? styles.publishedTogglePublished : styles.publishedToggleDraft,
                !canManage || saving ? styles.publishedToggleDisabled : '',
              ].filter(Boolean).join(' ')}
            >
              <input
                type="checkbox"
                checked={form.isPublished}
                disabled={!canManage || saving}
                onChange={(event) => setForm((current) => ({ ...current, isPublished: event.target.checked }))}
              />
              <span className={styles.publishedToggleContent}>
                <span className={styles.publishedToggleHeader}>
                  <span className={styles.publishedToggleTitle}>{t('publishedTitle')}</span>
                  <span
                    className={[
                      styles.publishedStatusBadge,
                      form.isPublished ? styles.publishedStatusBadgePublished : styles.publishedStatusBadgeDraft,
                    ].join(' ')}
                  >
                    {form.isPublished ? t('published') : t('draft')}
                  </span>
                </span>
                <span className={styles.publishedToggleHelp}>{t('publishedHelp')}</span>
              </span>
            </label>

            <ToolUseCaseAssignment
              selectedUseCaseIds={form.assignedUseCaseIds}
              disabled={!canManage || saving}
              isPublished={form.isPublished}
              onChange={(assignedUseCaseIds) => setForm((current) => ({ ...current, assignedUseCaseIds }))}
            />

            <div className="company-form-grid" style={{ marginTop: 12 }}>
              {!selectedId ? (
                <label>
                  {t('orchestratorId')}
                  <input
                    className="text-input"
                    value={form.orchestratorId}
                    disabled={!canManage || saving}
                    onChange={(event) => setForm((current) => ({ ...current, orchestratorId: event.target.value }))}
                  />
                </label>
              ) : null}

              <label>
                {t('displayName')}
                <input
                  className="text-input"
                  value={form.displayName}
                  disabled={!canManage || saving}
                  onChange={(event) => setForm((current) => ({ ...current, displayName: event.target.value }))}
                />
              </label>

              <label>
                {t('kernelFunctionName')}
                <input
                  className="text-input"
                  value={form.kernelFunctionName}
                  disabled={!canManage || saving}
                  onChange={(event) => setForm((current) => ({ ...current, kernelFunctionName: event.target.value }))}
                />
              </label>

              <label style={{ gridColumn: '1 / -1' }}>
                {t('description')}
                <textarea
                  className="text-input"
                  rows={3}
                  value={form.description}
                  disabled={!canManage || saving}
                  onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                />
              </label>

              <label style={{ gridColumn: '1 / -1' }}>
                {t('systemPrompt')}
                <textarea
                  className="text-input"
                  rows={5}
                  value={form.systemPrompt}
                  disabled={!canManage || saving}
                  onChange={(event) => setForm((current) => ({ ...current, systemPrompt: event.target.value }))}
                />
              </label>

              <label>
                {t('argumentMode')}
                <select
                  className="text-input"
                  value={form.argumentMode}
                  disabled={!canManage || saving}
                  onChange={(event) => setForm((current) => ({
                    ...current,
                    argumentMode: event.target.value as AiToolOrchestratorArgumentMode,
                  }))}
                >
                  <option value="forward">{t('argumentModeForward')}</option>
                  <option value="structured">{t('argumentModeStructured')}</option>
                  <option value="hybrid">{t('argumentModeHybrid')}</option>
                </select>
              </label>

              {form.argumentMode !== 'structured' ? (
                <label>
                  {t('forwardedParameterName')}
                  <input
                    className="text-input"
                    value={form.forwardedParameterName}
                    disabled={!canManage || saving}
                    onChange={(event) => setForm((current) => ({ ...current, forwardedParameterName: event.target.value }))}
                  />
                </label>
              ) : null}

              <label>
                {t('subagentProvider')}
                <select
                  className="text-input"
                  value={form.subagentChatProvider}
                  disabled={!canManage || saving}
                  onChange={(event) => {
                    const subagentChatProvider = event.target.value;
                    setForm((current) => ({
                      ...current,
                      subagentChatProvider,
                      subagentModelId: resolveSubagentModelIdForProvider(
                        providerSettings,
                        subagentChatProvider,
                        current.subagentModelId,
                      ),
                    }));
                  }}
                >
                  <option value="inherit">{t('providerInherit')}</option>
                  <option value="Auto">Auto</option>
                  <option value="AzureOpenAI">Azure OpenAI</option>
                  <option value="OpenAI">OpenAI</option>
                  <option value="DeepSeek">DeepSeek</option>
                </select>
              </label>

              <label>
                {t('subagentModel')}
                <select
                  className="text-input"
                  value={form.subagentModelId}
                  disabled={subagentModelSelectDisabled}
                  onChange={(event) => setForm((current) => ({ ...current, subagentModelId: event.target.value }))}
                >
                  {form.subagentChatProvider === 'inherit' ? (
                    <option value="">{t('subagentModelInherited')}</option>
                  ) : form.subagentChatProvider === 'Auto' ? (
                    <option value="">
                      {providerSettings?.activeChatModelId
                        ? t('subagentModelAutoResolved', {
                          provider: providerSettings.activeChatProvider,
                          model: providerSettings.activeChatModelId,
                        })
                        : t('subagentModelAuto')}
                    </option>
                  ) : subagentModelOptions.length === 0 ? (
                    <option value="">{t('subagentModelNoOptions')}</option>
                  ) : (
                    subagentModelOptions.map((option) => (
                      <option key={`${option.providerId}-${option.value}`} value={option.value}>
                        {option.label}
                      </option>
                    ))
                  )}
                  {form.subagentModelId
                    && isSubagentModelSelectable(form.subagentChatProvider)
                    && !subagentModelOptions.some((option) => option.value === form.subagentModelId) ? (
                      <option value={form.subagentModelId}>{form.subagentModelId}</option>
                    ) : null}
                </select>
                {form.subagentChatProvider !== 'inherit'
                  && form.subagentChatProvider !== 'Auto'
                  && subagentModelOptions.length === 0 ? (
                    <span className={styles.publishedToggleHelp}>{t('subagentModelNoOptionsHelp')}</span>
                  ) : null}
              </label>

              <label>
                {t('maxChildToolCalls')}
                <input
                  className="text-input"
                  type="number"
                  min={1}
                  max={20}
                  value={form.maxChildToolCalls}
                  disabled={!canManage || saving}
                  onChange={(event) => setForm((current) => ({ ...current, maxChildToolCalls: Number(event.target.value) }))}
                />
              </label>

              <label>
                {t('maxOutputTokens')}
                <input
                  className="text-input"
                  type="number"
                  min={1}
                  value={form.maxOutputTokens}
                  disabled={!canManage || saving}
                  onChange={(event) => setForm((current) => ({ ...current, maxOutputTokens: event.target.value }))}
                />
              </label>

              <label>
                {t('timeoutSeconds')}
                <input
                  className="text-input"
                  type="number"
                  min={1}
                  value={form.timeoutSeconds}
                  disabled={!canManage || saving}
                  onChange={(event) => setForm((current) => ({ ...current, timeoutSeconds: event.target.value }))}
                />
              </label>

            </div>

            {form.argumentMode !== 'forward' ? (
              <div style={{ marginTop: 16 }}>
                <div className="company-section-head">
                  <h4>{t('parameters')}</h4>
                  {canManage ? (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setForm((current) => ({ ...current, parameters: [...current.parameters, emptyParameter()] }))}
                    >
                      {t('addParameter')}
                    </button>
                  ) : null}
                </div>
                {form.parameters.map((parameter, index) => (
                  <div key={`parameter-${index}`} className="company-form-grid" style={{ marginTop: 8 }}>
                    <label>
                      {t('parameterName')}
                      <input
                        className="text-input"
                        value={parameter.name}
                        disabled={!canManage || saving}
                        onChange={(event) => setForm((current) => ({
                          ...current,
                          parameters: current.parameters.map((item, itemIndex) => (
                            itemIndex === index ? { ...item, name: event.target.value } : item
                          )),
                        }))}
                      />
                    </label>
                    <label>
                      {t('parameterDescription')}
                      <input
                        className="text-input"
                        value={parameter.description}
                        disabled={!canManage || saving}
                        onChange={(event) => setForm((current) => ({
                          ...current,
                          parameters: current.parameters.map((item, itemIndex) => (
                            itemIndex === index ? { ...item, description: event.target.value } : item
                          )),
                        }))}
                      />
                    </label>
                  </div>
                ))}
              </div>
            ) : null}

            <div style={{ marginTop: 16 }}>
              <h4>{t('childTools')}</h4>
              <p style={{ color: '#64748b' }}>{t('childToolsHelp')}</p>
              <div className="role-permission-list" style={{ marginTop: 8 }}>
                {assignableCatalog.map((item) => {
                  const checked = form.childFunctionIds.includes(item.functionId);
                  return (
                    <label key={item.functionId} className="role-permission-item">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!canManage || saving || (!item.isAssignableAsChild && !checked)}
                        onChange={() => toggleChildTool(item.functionId)}
                      />
                      <span>
                        <strong>{item.displayName}</strong>
                        <small>{item.functionId} · {item.source}</small>
                        {!item.isAssignableAsChild && item.assignedToOrchestratorName
                          ? <small>{t('assignedElsewhere', { name: item.assignedToOrchestratorName })}</small>
                          : null}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {canManage ? (
              <div className="company-actions-inline" style={{ marginTop: 16 }}>
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? t('saving', { ns: 'common' }) : t('save')}
                </button>
                {selectedId ? (
                  <button type="button" className="btn btn-secondary" onClick={handleDelete} disabled={saving}>
                    {t('delete')}
                  </button>
                ) : null}
              </div>
            ) : null}

            {selectedId ? (
              <div style={{ marginTop: 24 }}>
                <h4>{t('preview')}</h4>
                <div className="company-form-grid">
                  {argumentFields.map((field) => (
                    <label key={field.name}>
                      {field.name}
                      <input
                        className="text-input"
                        value={previewArgs[field.name] ?? ''}
                        disabled={previewing}
                        onChange={(event) => setPreviewArgs((current) => ({
                          ...current,
                          [field.name]: event.target.value,
                        }))}
                      />
                    </label>
                  ))}
                </div>
                <div className="company-actions-inline" style={{ marginTop: 12 }}>
                  <button type="button" className="btn btn-secondary" onClick={handlePreview} disabled={previewing}>
                    {previewing ? t('previewing') : t('runPreview')}
                  </button>
                </div>
                {previewResult ? (
                  <pre style={{ marginTop: 12, whiteSpace: 'pre-wrap', background: '#f8fafc', padding: 12, borderRadius: 8 }}>
                    {previewResult}
                  </pre>
                ) : null}
              </div>
            ) : null}
          </section>
        </div>
      )}
    </div>
  );
};

export default OrchestratorsPage;
